"use client";

import { useEffect, useRef, type ElementType, type ReactNode } from "react";

/**
 * Fades and lifts its children in once, when they first scroll into view.
 *
 * The in-state is a DOM attribute rather than React state: a reveal has no
 * reason to re-render anything, and the hidden state in globals.css only
 * applies when scripting is enabled, so nothing stays invisible if this
 * never runs. `d` staggers siblings in steps of 80ms.
 */
export default function Reveal({
  as: Tag = "div",
  d = 0,
  className,
  children,
}: {
  as?: ElementType;
  d?: number;
  className?: string;
  children: ReactNode;
}) {
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === "undefined") {
      el.setAttribute("data-in", "");
      return;
    }
    const io = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        el.setAttribute("data-in", "");
        io.disconnect();
      },
      { rootMargin: "0px 0px -8% 0px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <Tag ref={ref} className={`reveal ${className ?? ""}`} style={{ "--d": d } as React.CSSProperties}>
      {children}
    </Tag>
  );
}
